import type {
  BackfaceVisibility,
  IEffectsEntryAnimation,
  IFillStyle,
  IFilterItem,
  ILoopingAnimationItem,
  ILoopingKeyframeStop,
  IStylingEffects,
  ITransformItem,
  ITransitionItem,
  TransformStyle
} from "@kuika/kuika-cl-model";
import { v4 as uuidv4 } from "uuid";
import { ENTRY_ANIMATION_DEFAULT_DURATION, ENTRY_ANIMATION_DEFAULT_EASING } from "../styling-effects-helpers";
import type {
  EffectPreset,
  EffectPresetApplyMode,
  EffectPresetBasicPayload,
  EffectPresetEntryTiming,
  EffectPresetFillPayload,
  EffectPresetFilterInput,
  EffectPresetInitialStateHint,
  EffectPresetOutlinePayload,
  EffectPresetTransformInput,
  EffectPresetTransformSettings,
  EffectPresetTransitionInput
} from "./effects-presets-types";

export interface ApplyEffectPresetOptions {
  mode?: EffectPresetApplyMode;
  kuikaid?: string;
}

interface EffectsBucket {
  opacity?: number;
  cursor?: string;
  pointerEvents?: string;
  mixBlendMode?: string;
  outlineStyle?: string;
  outlineWidth?: number;
  outlineOffset?: number;
  outlineColor?: string;
  transforms?: ITransformItem[];
  transformOriginX?: number;
  transformOriginY?: number;
  transformStyle?: TransformStyle;
  backfaceVisibility?: BackfaceVisibility;
  perspective?: number;
  childrenPerspective?: number;
  childrenPerspectiveOriginX?: number;
  childrenPerspectiveOriginY?: number;
  transitions?: ITransitionItem[];
  filters?: IFilterItem[];
  backdropFilters?: IFilterItem[];
  animations?: ILoopingAnimationItem[];
}

const cloneEffects = (effects: IStylingEffects | undefined, kuikaid: string): IStylingEffects => {
  if (!effects) {
    return { effectsId: null, kuikaid };
  }
  return JSON.parse(JSON.stringify(effects)) as IStylingEffects;
};

const writeBasic = (bucket: EffectsBucket, payload: EffectPresetBasicPayload | undefined, replace: boolean): void => {
  if (!payload && !replace) return;
  if (replace) {
    bucket.opacity = undefined;
    bucket.cursor = undefined;
    bucket.pointerEvents = undefined;
    bucket.mixBlendMode = undefined;
  }
  if (!payload) return;
  if (payload.opacity !== undefined) bucket.opacity = payload.opacity;
  if (payload.cursor !== undefined) bucket.cursor = payload.cursor;
  if (payload.pointerEvents !== undefined) bucket.pointerEvents = payload.pointerEvents;
  if (payload.mixBlendMode !== undefined) bucket.mixBlendMode = payload.mixBlendMode;
};

const writeOutline = (
  bucket: EffectsBucket,
  payload: EffectPresetOutlinePayload | undefined,
  replace: boolean
): void => {
  if (!payload && !replace) return;
  if (replace) {
    bucket.outlineStyle = undefined;
    bucket.outlineWidth = undefined;
    bucket.outlineOffset = undefined;
    bucket.outlineColor = undefined;
  }
  if (!payload) return;
  if (payload.outlineStyle !== undefined) bucket.outlineStyle = payload.outlineStyle;
  if (payload.outlineWidth !== undefined) bucket.outlineWidth = payload.outlineWidth;
  if (payload.outlineOffset !== undefined) bucket.outlineOffset = payload.outlineOffset;
  if (payload.outlineColor !== undefined) bucket.outlineColor = payload.outlineColor;
};

const writeTransformSettings = (
  bucket: EffectsBucket,
  payload: EffectPresetTransformSettings | undefined,
  replace: boolean
): void => {
  if (!payload && !replace) return;
  if (replace) {
    bucket.transformOriginX = undefined;
    bucket.transformOriginY = undefined;
    bucket.transformStyle = undefined;
    bucket.backfaceVisibility = undefined;
    bucket.perspective = undefined;
    bucket.childrenPerspective = undefined;
    bucket.childrenPerspectiveOriginX = undefined;
    bucket.childrenPerspectiveOriginY = undefined;
  }
  if (!payload) return;
  if (payload.transformOriginX !== undefined) bucket.transformOriginX = payload.transformOriginX;
  if (payload.transformOriginY !== undefined) bucket.transformOriginY = payload.transformOriginY;
  if (payload.transformStyle !== undefined) bucket.transformStyle = payload.transformStyle;
  if (payload.backfaceVisibility !== undefined) bucket.backfaceVisibility = payload.backfaceVisibility;
  if (payload.perspective !== undefined) bucket.perspective = payload.perspective;
  if (payload.childrenPerspective !== undefined) bucket.childrenPerspective = payload.childrenPerspective;
  if (payload.childrenPerspectiveOriginX !== undefined)
    bucket.childrenPerspectiveOriginX = payload.childrenPerspectiveOriginX;
  if (payload.childrenPerspectiveOriginY !== undefined)
    bucket.childrenPerspectiveOriginY = payload.childrenPerspectiveOriginY;
};

const hydrateTransforms = (items: EffectPresetTransformInput[]): ITransformItem[] =>
  items.map((it: EffectPresetTransformInput): ITransformItem => ({ ...it, id: uuidv4() }));

const hydrateFilters = (items: EffectPresetFilterInput[]): IFilterItem[] =>
  items.map((it: EffectPresetFilterInput): IFilterItem => ({ ...it, id: uuidv4() }));

const hydrateTransitions = (items: EffectPresetTransitionInput[]): ITransitionItem[] =>
  items.map((it: EffectPresetTransitionInput): ITransitionItem => ({ ...it, id: uuidv4() }));

// Re-mint the item id AND every nested transform/filter id so a bundled looping
// animation applied to two components never shares an item or keyframe-item id.
const hydrateAnimations = (items: ILoopingAnimationItem[]): ILoopingAnimationItem[] =>
  items.map((it: ILoopingAnimationItem): ILoopingAnimationItem => ({
    ...it,
    id: uuidv4(),
    keyframes: (it.keyframes ?? []).map((stop: ILoopingKeyframeStop): ILoopingKeyframeStop => ({
      ...stop,
      transforms: stop.transforms?.map((t: ITransformItem): ITransformItem => ({ ...t, id: uuidv4() })),
      filters: stop.filters?.map((f: IFilterItem): IFilterItem => ({ ...f, id: uuidv4() })),
      backdropFilters: stop.backdropFilters?.map((f: IFilterItem): IFilterItem => ({ ...f, id: uuidv4() }))
    }))
  }));

/**
 * Translate a reveal preset's `initialStateHint` (the "from" offset) into an
 * entry-animation spec the React generator emits as real `@keyframes`. The
 * preset's `transitions` durations (200-300ms) were tuned for the old
 * state-transition mechanism and feel rushed as a mount animation, so entry
 * animations use a single graceful default (`ENTRY_ANIMATION_DEFAULT_DURATION`)
 * unless the preset declares its own `entryTiming` (character reveals like the
 * overshoot "pop" need their bespoke duration/bezier); users fine-tune via the
 * Effects panel's Entry Animation control either way.
 */
const buildEntryAnimation = (
  hint: EffectPresetInitialStateHint,
  timing: EffectPresetEntryTiming | undefined
): IEffectsEntryAnimation => {
  return {
    fromOpacity: hint.opacity,
    fromTranslateX: hint.translateX,
    fromTranslateY: hint.translateY,
    fromScale: hint.scale,
    fromRotateZ: hint.rotateZ,
    durationMs: timing?.durationMs ?? ENTRY_ANIMATION_DEFAULT_DURATION,
    timingFunction: timing?.timingFunction ?? ENTRY_ANIMATION_DEFAULT_EASING,
    delayMs: timing?.delayMs ?? 0
  };
};

/**
 * Apply an effect preset to an existing IStylingEffects object.
 *
 * Payload always writes to the root effects fields (`opacity`, `transforms`,
 * `filters`, ...). State-bucket routing (`hoverStyles`, `pressedStyles`,
 * ...) is the caller's responsibility — it must switch the styling-window
 * into the preset's target state BEFORE calling `form.submit()` so the
 * pipeline's `saveFormDataToStateStyles` lifts the values into the right
 * bucket. Writing into a state bucket here would be overwritten by
 * `preserveStateStylesFromSource` during the same submit when state mode is
 * "none" (KOA-15838 invariant).
 *
 * - Multi-item arrays (transforms / filters / backdropFilters / transitions)
 *   from the preset payload replace the corresponding array in full; each
 *   item gets a fresh uuid.
 * - Scalar fields are overwritten only when the payload defines them.
 * - In `mode: "replace"`, scalars in groups the preset touches are first
 *   reset to `undefined` so leftover values from the previous configuration
 *   don't bleed through.
 * - Identity fields on the root (`effectsId`, `effectsIds`, `kuikaid`,
 *   `styleType`, `name`, `componentType`, `overridden`, `groupId`) and all
 *   persisted state sub-objects (`hoverStyles`, `pressedStyles`, ...) the
 *   preset does not touch are preserved.
 */
export const applyEffectPreset = (
  currentEffects: IStylingEffects | undefined,
  preset: EffectPreset,
  options?: ApplyEffectPresetOptions
): IStylingEffects => {
  const mode: EffectPresetApplyMode = options?.mode ?? "merge";
  const replace: boolean = mode === "replace";
  const kuikaid: string = options?.kuikaid ?? currentEffects?.kuikaid ?? "";

  const next: IStylingEffects = cloneEffects(currentEffects, kuikaid);
  const bucket: EffectsBucket = next as EffectsBucket;

  writeBasic(bucket, preset.payload.basic, replace && preset.payload.basic !== undefined);
  writeOutline(bucket, preset.payload.outline, replace && preset.payload.outline !== undefined);
  writeTransformSettings(
    bucket,
    preset.payload.transformSettings,
    replace && preset.payload.transformSettings !== undefined
  );

  // Multi-item arrays: a defined payload always replaces the bucket array in
  // full. `replace` mode does not extend to arrays the preset doesn't mention
  // — clearing unrelated arrays would surprise the user.
  if (preset.payload.transforms !== undefined) {
    bucket.transforms = hydrateTransforms(preset.payload.transforms);
  }
  if (preset.payload.filters !== undefined) {
    bucket.filters = hydrateFilters(preset.payload.filters);
  }
  if (preset.payload.backdropFilters !== undefined) {
    bucket.backdropFilters = hydrateFilters(preset.payload.backdropFilters);
  }
  if (preset.payload.transitions !== undefined) {
    bucket.transitions = hydrateTransitions(preset.payload.transitions);
  }
  if (preset.payload.animations !== undefined) {
    bucket.animations = hydrateAnimations(preset.payload.animations);
  }

  // Reveal presets ship only their final resting state; without a real mount
  // animation they generate to a no-op (the element just sits at rest). Derive
  // an entry animation from the initialStateHint so the generator emits
  // @keyframes. Only base-targeted reveal presets carry a hint.
  const hint: EffectPresetInitialStateHint | undefined = preset.initialStateHint;
  if (hint && preset.targetState === "base") {
    next.entryAnimation = buildEntryAnimation(hint, preset.entryTiming);
  } else if (replace) {
    next.entryAnimation = null;
  }

  return next;
};

const cloneFill = (fill: IFillStyle | undefined, kuikaid: string): IFillStyle => {
  if (!fill) {
    return { fillId: null, fillIds: null, kuikaid };
  }
  return JSON.parse(JSON.stringify(fill)) as IFillStyle;
};

/**
 * Companion to {@link applyEffectPreset} for glass/translucent presets.
 *
 * `backdrop-filter` only renders where the element's own background is
 * transparent; an opaque named fill (fillId) covers the backdrop and the
 * blur looks like a no-op. Effect payloads can't reach the fill section, so
 * a preset declares its translucent background via `payload.fill` and this
 * pure helper produces the next fill: the colour is written as an INLINE
 * `color` (which wins over the named fill because the component's own fill
 * object is merged last in StyleHelper.getFillStyles) and the named fill is
 * detached (`fillId`/`fillIds` → null), mirroring the custom-colour path in
 * styling-fill-component. Any inherited background image is cleared so the
 * glass surface is unambiguous.
 *
 * Returns `undefined` when the preset declares no fill payload, so the caller
 * leaves the element's existing fill completely untouched.
 */
export const applyEffectPresetFill = (
  currentFill: IFillStyle | undefined,
  preset: EffectPreset,
  kuikaid: string
): IFillStyle | undefined => {
  const fillPayload: EffectPresetFillPayload | undefined = preset.payload.fill;
  if (!fillPayload || fillPayload.backgroundColor === undefined) {
    return undefined;
  }
  const next: IFillStyle = cloneFill(currentFill, kuikaid);
  next.color = fillPayload.backgroundColor;
  next.fillId = null;
  next.fillIds = null;
  next.backgroundImage = undefined;
  return next;
};
